import { Box, Toolbar } from "@mui/material";
import Logo from "@components/Logo/Logo";
import NotificationsDropdown from "@components/NotificationsDropdown/NotificationsDropdown.tsx";
import type { NotificationItem } from "@components/NotificationsDropdown/NotificationsDropdown.types.ts";
import { useLogout } from "@hooks/useLogout";
import { mapApiToUi } from "@services/notifications/mapper.ts";
import { buildNotificationDetailsPath } from "@services/notifications/routeHelpers";
import { fetchTerminalLocations } from "@services/notifications/terminalNameResolver";
import type { NotificationItemApi } from "@services/notifications/types";
import { useNotificationIndicator } from "@services/notifications/useNotificationIndicator";
import { getNotifications, markNotificationsSeen } from "@services/notificationsApi";
import { getPortServicesByIds } from "@services/portServicesApi";
import { useQuery } from "@tanstack/react-query";
import { startScreenNavigation } from "@telemetry/navigationTiming";
import { paths } from "@utils/navigation.tsx";
import { useNavigate } from "react-router-dom";
import AccountMenu from "./AccountMenu";

const Header = () => {
  const navigate = useNavigate();
  const { logout } = useLogout();

  const { data: notifications = [] } = useQuery<NotificationItem[]>({
    queryKey: ["notifications.dropdown.unseen"],
    queryFn: async () => {
      const [res, terminalLocations] = await Promise.all([
        getNotifications({
          service: "bff-notification",
          page: 0,
          size: 3,
          checkUserSession: true,
        }),
        fetchTerminalLocations("bff-vessel-call").catch(() => []), // Fallback to empty array on error
      ]);
      const records = res.records ?? [];
      // Log user out if their account has been disabled, the user's token is not correct and the notification is received
      const hasUserDisabledNotification = records.some((record) => record.type === "USER_DISABLED");
      const refreshNotificationIds = records
        .filter((record) => record.type === "REFRESH")
        .map((record) => record.id);

      if (hasUserDisabledNotification) {
        await logout();
        window.location.reload();
      } else if (refreshNotificationIds.length > 0) {
        await markNotificationsSeen({
          service: "bff-notification",
          ids: refreshNotificationIds,
        }).catch(() => undefined);
        window.location.reload();
      }

      const portServiceIds = Array.from(
        new Set(
          records
            .filter((n) => n.relatedEntityType === "PORT_SERVICES" && !!n.relatedEntityId)
            .map((n) => n.relatedEntityId as string),
        ),
      );

      const portServices = await getPortServicesByIds({
        service: "bff-vessel-call",
        ids: portServiceIds,
        page: 0,
        pageSize: 50,
      }).catch(() => []);

      const portServicesById = new Map(portServices.map((service) => [service.id, service]));
      const mapped = records.map((r: NotificationItemApi) => {
        const entityData =
          r.relatedEntityType === "PORT_SERVICES" && r.relatedEntityId
            ? (portServicesById.get(r.relatedEntityId) ?? null)
            : null;

        return mapApiToUi(r, terminalLocations, entityData);
      });

      return mapped;
    },
    staleTime: 60_000,
    refetchInterval: 30_000,
  });

  const { hasNew, acknowledgeNow } = useNotificationIndicator(notifications);

  return (
    <Toolbar disableGutters sx={{ minHeight: "0 !important", padding: "12px 0 8px" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
        }}
      >
        <div>
          <Logo height={40} />
        </div>

        <div className="flex items-center gap-2">
          <NotificationsDropdown
            notifications={notifications}
            hasNew={hasNew}
            onOpen={acknowledgeNow}
            onViewAll={() => {
              startScreenNavigation("Header", window.location.pathname, "Notifications");
              navigate(paths.notifications);
            }}
            onItemClick={(item) => {
              const path = buildNotificationDetailsPath(
                item.relatedEntityType,
                item.relatedEntityId,
                item.secondRelatedEntityId,
              );
              if (path) navigate(path);
            }}
          />
          <AccountMenu />
        </div>
      </Box>
    </Toolbar>
  );
};

export default Header;
